import type { Layer, LayerMaskData } from 'ag-psd';
import type { BoxEffects } from './custom-template';

export type PsdLayerKind =
  | 'group'
  | 'pixel'
  | 'text'
  | 'smartObject'
  | 'vector'
  | 'fill'
  | 'adjustment'
  | 'background'
  | 'video'
  | 'unknown';

export type PsdLayerNodeKind = PsdLayerKind;

export type PsdLayerAdjustmentSubKind =
  | 'brightness'
  | 'curves'
  | 'levels'
  | 'hueSaturation'
  | 'colorBalance'
  | 'exposure'
  | 'vibrance'
  | 'photoFilter'
  | 'channelMixer'
  | 'colorLookup'
  | 'invert'
  | 'posterize'
  | 'threshold'
  | 'gradientMap'
  | 'selectiveColor'
  | 'blackAndWhite';

export type PsdLayerFillSubKind = 'solid' | 'gradient' | 'pattern';

export type PsdSmartObjectSubKind = 'embedded' | 'linked-local' | 'linked-library';

export type PsdSmartObjectState = 'up-to-date' | 'out-of-date' | 'missing';

export interface PsdSmartFilter {
  readonly name: string;
  readonly enabled: boolean;
  readonly lost?: true;
}

export interface PsdLayerMaskInfo {
  readonly kind: 'pixel' | 'vector';
  readonly enabled: boolean;
  readonly linked: boolean;
}

export type PsdLayerEffectKind =
  | 'dropShadow'
  | 'innerShadow'
  | 'outerGlow'
  | 'innerGlow'
  | 'stroke'
  | 'satin'
  | 'colorOverlay'
  | 'gradientOverlay'
  | 'patternOverlay'
  | 'bevelEmboss'
  | 'bevelContour'
  | 'bevelTexture'
  | 'feather';

export interface PsdLayerEffectInfo {
  readonly kind: PsdLayerEffectKind;
  readonly enabled: boolean;
  readonly instanceIndex?: number;
  /** Written only when the engine cannot draw this instance live — the match key of `UNSUPPORTED_FRAME_EFFECTS`. */
  readonly fill?: 'gradient' | 'pattern';
  /** The import draws this enabled instance neither live nor baked: it is not on the spread. */
  readonly lost?: true;
}

/** Effects the live layer-style path refuses (`liveStyleRefusals`): on a photo frame they are not carried over. */
export const UNSUPPORTED_FRAME_EFFECTS: ReadonlyArray<Required<Pick<PsdLayerEffectInfo, 'kind' | 'fill'>>> = [
  { kind: 'gradientOverlay', fill: 'gradient' },
  { kind: 'outerGlow', fill: 'gradient' },
  { kind: 'innerGlow', fill: 'gradient' },
  { kind: 'stroke', fill: 'gradient' },
  { kind: 'stroke', fill: 'pattern' },
  { kind: 'bevelTexture', fill: 'pattern' },
  { kind: 'patternOverlay', fill: 'pattern' },
];

const UNSUPPORTED_TEXT_EFFECT_KINDS: readonly PsdLayerEffectKind[] = ['bevelEmboss', 'satin', 'patternOverlay'];

/**
 * Enabled PSD-tree effects of a text layer its box does not carry: the import dropped them. Inner glow is out — the
 * import carries every one, its absence means the user removed it. Photoshop panel order.
 */
export function unsupportedTextEffects(
  effects: BoxEffects | undefined,
  node?: Pick<PsdLayerNode, 'effects'>,
): PsdLayerEffectKind[] {
  const carried: Partial<Record<PsdLayerEffectKind, boolean>> = {
    bevelEmboss: !!effects?.bevel,
    satin: !!effects?.satin,
    patternOverlay: !!effects?.patternOverlay,
  };
  const found = new Set(node?.effects?.filter(e => e.enabled && !carried[e.kind]).map(e => e.kind));
  return UNSUPPORTED_TEXT_EFFECT_KINDS.filter(kind => found.has(kind));
}

export interface PsdLayerLockState {
  readonly transparency?: boolean;
  readonly image?: boolean;
  readonly position?: boolean;
  readonly all?: boolean;
}

export type PsdLayerColorLabel = 'red' | 'orange' | 'yellow' | 'green' | 'blue' | 'violet' | 'gray';

export type PsdGroupSource = Pick<
  Layer,
  | 'fillOpacity'
  | 'knockout'
  | 'knockoutDeep'
  | 'blendClippendElements'
  | 'blendInteriorElements'
  | 'transparencyShapesLayer'
  | 'channelBlendingRestrictions'
  | 'blendingRanges'
  | 'layerMaskAsGlobalMask'
  | 'effects'
  | 'vectorMask'
  | 'artboard'
> & {
  readonly mask?: Omit<LayerMaskData, 'canvas' | 'imageData'>;
  readonly realMask?: Omit<LayerMaskData, 'canvas' | 'imageData'>;
};

export interface PsdLayerNode {
  readonly groupSource?: PsdGroupSource;
  readonly id: string;
  readonly name: string;
  readonly kind: PsdLayerKind;
  readonly visible: boolean;
  readonly opacity?: number;
  readonly blendMode?: string;
  readonly bounds?: {
    readonly left: number;
    readonly top: number;
    readonly right: number;
    readonly bottom: number;
  };
  readonly boxRef?: string;
  readonly children?: readonly PsdLayerNode[];

  readonly adjustmentSubKind?: PsdLayerAdjustmentSubKind;
  readonly fillSubKind?: PsdLayerFillSubKind;
  readonly textPreview?: string;
  readonly textWarped?: boolean;

  readonly smartObjectSubKind?: PsdSmartObjectSubKind;
  readonly smartObjectState?: PsdSmartObjectState;
  readonly smartObjectSourceFileName?: string;
  readonly smartFilters?: readonly PsdSmartFilter[];

  readonly pixelMask?: PsdLayerMaskInfo;
  readonly vectorMask?: PsdLayerMaskInfo;
  readonly isClipped?: boolean;
  readonly isClippingBase?: boolean;

  readonly effects?: readonly PsdLayerEffectInfo[];
  readonly allEffectsEnabled?: boolean;

  readonly lock?: PsdLayerLockState;
  readonly colorLabel?: PsdLayerColorLabel;
}
